import { useEffect, useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Clock3,
  KeyRound,
  Landmark,
  LoaderCircle,
  Settings,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { CopyableDate, PageHeader, useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";

function formatDate(value) {
  if (!value) return "Fecha no informada";
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: "America/Argentina/Mendoza",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function CredentialsForm({
  currentUsername = "",
  saving,
  onCancel,
  onSubmit,
}) {
  const [username, setUsername] = useState(currentUsername);
  const [password, setPassword] = useState("");

  return (
    <form
      className="integration-config-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ username, password });
      }}
    >
      <div className="integration-config-heading">
        <span><KeyRound size={20} /></span>
        <div>
          <h2>Credenciales del PJN</h2>
          <p>
            Ingresá los mismos datos que usás en el Portal de Gestión de
            Causas del Poder Judicial de la Nación.
          </p>
        </div>
      </div>
      <label>
        <span>CUIL o CUIT</span>
        <input
          autoComplete="username"
          inputMode="numeric"
          placeholder="20-12345678-9"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          required
        />
      </label>
      <label>
        <span>Contraseña</span>
        <input
          autoComplete="current-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
      </label>
      <div className="integration-credential-note">
        <ShieldCheck size={17} />
        <span>
          La contraseña se guarda cifrada y nunca se muestra ni se devuelve
          desde la API.
        </span>
      </div>
      <div className="integration-form-actions">
        {onCancel && (
          <button className="secondary-button" type="button" onClick={onCancel}>
            Cancelar
          </button>
        )}
        <button className="primary-button" disabled={saving}>
          {saving && <LoaderCircle className="spin" size={17} />}
          {saving ? "Guardando…" : "Guardar credenciales"}
        </button>
      </div>
    </form>
  );
}

export function PjnScwIntegrationPage() {
  const api = useApi();
  const { navigate } = useRouter();
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    api.getPjnScwIntegration()
      .then((result) => {
        if (active) setIntegration(result.integration);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  const saveCredentials = async (credentials) => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const result = await api.configurePjnScwIntegration(credentials);
      setIntegration(result.integration);
      setEditing(false);
      setMessage("Las credenciales del PJN se guardaron de forma segura.");
      window.dispatchEvent(new Event("praxis:integrations-changed"));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(
      "¿Querés quitar la integración y borrar las credenciales guardadas?",
    )) return;
    setRemoving(true);
    setError("");
    setMessage("");
    try {
      await api.removePjnScwIntegration();
      setIntegration(null);
      setEditing(false);
      setMessage("La integración del PJN fue eliminada.");
      window.dispatchEvent(new Event("praxis:integrations-changed"));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="page integration-detail-page">
        <div className="integrations-loading">
          <LoaderCircle className="spin" size={24} />
          <span>Abriendo integración…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page integration-detail-page">
      <button
        className="integration-back"
        type="button"
        onClick={() => navigate("/integraciones")}
      >
        <ArrowLeft size={15} />
        Integraciones
      </button>
      <PageHeader
        eyebrow="Integraciones"
        title="Consulta de Expedientes del PJN"
        description="Guardá tus credenciales del Portal PJN para habilitar la consulta de expedientes federales desde Praxis."
      />

      {error && <div className="integration-feedback is-error">{error}</div>}
      {message && (
        <div className="integration-feedback is-success">
          <CheckCircle2 size={16} />
          {message}
        </div>
      )}

      {!integration || editing ? (
        <CredentialsForm
          currentUsername={integration?.username}
          saving={saving}
          onCancel={integration ? () => setEditing(false) : null}
          onSubmit={saveCredentials}
        />
      ) : (
        <section className="integration-status-card">
          <div className="integration-status-main">
            <span className="integration-status-icon"><Landmark size={23} /></span>
            <div>
              <span className="integration-state is-configured">
                Credenciales guardadas
              </span>
              <strong>CUIL/CUIT {integration.username}</strong>
              <small>
                <Clock3 size={13} />
                Configurada:{" "}
                <CopyableDate value={integration.configuredAt}>
                  {formatDate(integration.configuredAt)}
                </CopyableDate>
              </small>
            </div>
          </div>
          <div className="integration-status-actions">
            <button
              className="secondary-button"
              type="button"
              onClick={() => setEditing(true)}
            >
              <Settings size={16} />
              Cambiar credenciales
            </button>
            <button
              className="integration-remove-button"
              type="button"
              disabled={removing}
              onClick={remove}
              aria-label="Quitar integración"
            >
              {removing
                ? <LoaderCircle className="spin" size={16} />
                : <Trash2 size={16} />}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
